import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

import type { User } from '~/types/user';

import { useUpdateActivities, useUpdateVatRegulation, useUser } from '~/api/user';
import { Button, ExternalLink, Separator } from '~/components';
import { Activity, getSecondaryActivityOptions } from '~/constants/constants';
import { AnchorTargets } from '~/constants/url';
import { EditFinancialInfoForm } from '~/features/account';
import { useDialog } from '~/hooks/useDialog';
import { filterBoolean } from '~/utils/arrays';
import { noop } from '~/utils/functions';
import { toast } from '~/utils/toast';

import type { ActivitiesFormType } from './Activities/types';
import type { VatRegulationFormType } from './VatRegulation/VatRegulation.types';

import { SettingsBlocker } from '../components/SettingsBlocker';
import { SettingsSaveAction } from '../components/SettingsSaveAction';
import { SettingsSection, SettingsSections } from '../components/SettingsSection';
import { Accountant } from './Accountant/Accountant';
import { Activities } from './Activities/Activities';
import { BrandsTable } from './Brands/BrandsTable';
import { CreateBrandModal } from './Brands/CreateEditBrandModal/CreateBrandModal';
import { CompanyInfo, CompanyInfoLastUpdated, CompanyInfoRefreshAction } from './CompanyInfo/CompanyInfo';
import { VatRegulation } from './VatRegulation/VatRegulation';

const toActivitiesFormValues = (user: User): ActivitiesFormType => {
  const secondaryActivities = user.activities.filter((activity) => !activity.isPrimaryActivity);
  const secondaryActivityOptions = getSecondaryActivityOptions(secondaryActivities.map(({ id }) => id));

  return {
    primaryActivity: user.activities.find((activity) => activity.isPrimaryActivity)?.id ?? null,
    secondaryActivities: secondaryActivityOptions.map((activity) => secondaryActivities.map(({ id }) => id).includes(activity)),
    otherActivityInformation: user.activities.find((activity) => activity.id === Activity.other)?.extraInformation ?? '',
  };
};

export const Company = () => {
  const { data: user } = useUser();

  const updateActivitiesMutation = useUpdateActivities();
  const updateVatRegulationMutation = useUpdateVatRegulation();

  const { t } = useTranslation(['common', 'settings']);

  const createBrandDialog = useDialog();

  const activitiesForm = useForm<ActivitiesFormType>({ defaultValues: toActivitiesFormValues(user) });
  const vatRegulationForm = useForm<VatRegulationFormType>({ defaultValues: { vatExempt: user.vatExempt } });

  const saveActivities = async (data: ActivitiesFormType) => {
    invariant(data.primaryActivity, 'Expected primaryActivity to be defined.');

    const secondaryActivityOptions = getSecondaryActivityOptions(
      user.activities.filter((activity) => !activity.isPrimaryActivity).map(({ id }) => id),
    );

    const updatedUser = await updateActivitiesMutation.mutateAsync({
      primaryActivity: data.primaryActivity,
      secondaryActivities: data.secondaryActivities
        .map((selected, i) => (selected ? secondaryActivityOptions[i] : null))
        .filter(filterBoolean),
      otherActivityInformation: data.otherActivityInformation,
    });

    activitiesForm.reset(toActivitiesFormValues(updatedUser));
  };

  const saveVatRegulation = async ({ vatExempt }: VatRegulationFormType) => {
    invariant(vatExempt !== null, 'Expected vatExempt to be defined.');

    await updateVatRegulationMutation.mutateAsync({ vatExempt });

    vatRegulationForm.reset({ vatExempt });
  };

  const isDirty = activitiesForm.formState.isDirty || vatRegulationForm.formState.isDirty;
  const isSaving = updateActivitiesMutation.isPending || updateVatRegulationMutation.isPending;

  const onSave = async () => {
    const validity = await Promise.all([
      !activitiesForm.formState.isDirty || activitiesForm.trigger(),
      !vatRegulationForm.formState.isDirty || vatRegulationForm.trigger(),
    ]);

    if (validity.includes(false)) {
      // Marks the forms as submitted to trigger RHF native behavior (revalidation mode + focus first error).
      activitiesForm.handleSubmit(noop)();
      vatRegulationForm.handleSubmit(noop)();

      toast.danger({ description: t('settings:alerts.invalidFields') });

      return false;
    }

    const [activities, vatRegulation] = await Promise.allSettled([
      activitiesForm.formState.isDirty && saveActivities(activitiesForm.getValues()),
      vatRegulationForm.formState.isDirty && saveVatRegulation(vatRegulationForm.getValues()),
    ]);

    if (activities.status === 'rejected') {
      toast.danger({ description: t('settings:alerts.errorActivities') });
    }
    if (vatRegulation.status === 'rejected') toast.danger({ description: t('settings:accountCompletionModal.vatRegulation.error') });

    const isSaved = activities.status === 'fulfilled' && vatRegulation.status === 'fulfilled';

    if (isSaved) toast.success({ description: t('settings:alerts.successUpdated') });

    return isSaved;
  };

  const onDiscard = () => {
    activitiesForm.reset();
    vatRegulationForm.reset();
  };

  return (
    <>
      <SettingsSaveAction isDirty={isDirty} isSaving={isSaving} onSave={onSave} />

      <SettingsSections>
        <SettingsSection
          action={<CompanyInfoRefreshAction />}
          description={
            <>
              {t('settings:profile.info.kboDescription')}
              <CompanyInfoLastUpdated />
            </>
          }
          id={AnchorTargets.SETTINGS_COMPANY_INFO}
          title={t('settings:profile.info.companyTitle')}
        >
          <CompanyInfo />
        </SettingsSection>

        <Separator />

        <SettingsSection id={AnchorTargets.SETTINGS_COMPANY_ACTIVITIES} title={t('settings:profile.activities.title')}>
          <Activities form={activitiesForm} />
        </SettingsSection>

        <Separator />

        <SettingsSection id={AnchorTargets.SETTINGS_COMPANY_FINANCIAL} title={t('settings:profile.financial.title')}>
          <EditFinancialInfoForm user={user} />
        </SettingsSection>

        <Separator />

        <SettingsSection
          description={<Trans components={{ a: <ExternalLink /> }} i18nKey="settings:profile.vatRegulation.description" />}
          id={AnchorTargets.SETTINGS_COMPANY_VAT_REGULATION}
          title={t('settings:profile.vatRegulation.title')}
        >
          <VatRegulation form={vatRegulationForm} />
        </SettingsSection>

        <Separator />

        <SettingsSection
          action={
            <Button icon="Add" onClick={() => createBrandDialog.open()}>
              {t('settings:profile.brands.add')}
            </Button>
          }
          description={<Trans components={{ a: <ExternalLink /> }} i18nKey="settings:profile.brands.description" />}
          id={AnchorTargets.SETTINGS_COMPANY_BRANDS}
          title={t('settings:profile.brands.title')}
        >
          <BrandsTable />

          {createBrandDialog.isOpen && <CreateBrandModal onClose={createBrandDialog.close} />}
        </SettingsSection>

        {user.accountant && (
          <>
            <Separator />

            <SettingsSection
              description={<Trans components={{ a: <span /> }} i18nKey="settings:integrations.accountant.description" />}
              id={AnchorTargets.SETTINGS_COMPANY_ACCOUNTANT}
              title={t('settings:integrations.accountant.title')}
            >
              <Accountant accountant={user.accountant} />
            </SettingsSection>
          </>
        )}
      </SettingsSections>

      <SettingsBlocker isDirty={isDirty} isSaving={isSaving} onDiscard={onDiscard} onSave={onSave} />
    </>
  );
};
